import PartnerHomeGuard from './components/PartnerHomeGuard';
import './config/partnerThemes'; // runs the single-coach redirect on every page
import "./App.css";
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Page imports
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import HomeAppSelection from './pages/HomeAppSelection';
import Dashboard from './pages/Dashboard';
import TransformTest from './pages/TransformTest';
import EmitDashboard from './pages/EmitDashboard';
import MinimalDashboard from './pages/MinimalDashboard';
import EmitResearchChat from "./pages/emit-research/EmitResearchChat";
import EmitResearchOnboarding from "./pages/emit-research/EmitResearchOnboarding";
import ChroniclesStudioChat from "./pages/mindstreams-studio/ChroniclesStudioChat";
import ChroniclesStudioOnboarding from "./pages/mindstreams-studio/ChroniclesStudioOnboarding";
import StudioSoundboard from "./pages/mindstreams-studio/StudioSoundboard";
import HeroPage from './pages/HeroPage';
import { AppLanding, AppsIndex } from './pages/AppLanding';
import PlatformPage from './pages/PlatformPage';
import InvestorsPage from './pages/InvestorsPage';
import VCPage from './pages/VCPage';
import DebugTransform from './pages/DebugTransform';
import VerifyEmail from './pages/VerifyEmail';
import ParentConsent from './pages/ParentConsent';
import ParentConsentForm from './pages/ParentConsentForm';
import AdminConsents from './pages/AdminConsents';
import CareDesk from './pages/CareDesk';
import LeadsDashboard from './pages/LeadsDashboard';

// Family Coach imports
import FamilyOnboarding from './pages/FamilyCoach/FamilyOnboarding';
import FamilyCoachChat from './pages/FamilyCoach/FamilyCoachChat';
// Mental Performance imports
import MentalPerformanceOnboarding from './pages/mental-performance/MentalPerformanceOnboarding';
import MentalPerformanceChat from './pages/mental-performance/MentalPerformanceChat';
import SAFEOnboarding from './pages/safe/SAFEOnboarding';
import SAFEChat from './pages/safe/SAFEChat';
import JourneyDashboard from './pages/JourneyDashboard';
import JourneyRulesPage from './pages/JourneyRulesPage';
// Emit Profile
import EmitProfileSetup from './pages/emit/EmitProfileSetup';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';
import WhyEmit from './pages/WhyEmit';
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import HCSMPartnership from "./pages/partnerships/HCSMPartnership";
import EBSPartnership from "./pages/partnerships/EBSPartnership";
import HRMPartnership from "./pages/partnerships/HRMPartnership";
import PEOPartnership from "./pages/partnerships/PEOPartnership";
import InsureCoPartnership from "./pages/partnerships/InsureCoPartnership";
import RehabPartnership from "./pages/partnerships/RehabPartnership";
import ChaplainPartnership from "./pages/partnerships/ChaplainPartnership";
import BookstorePartnership from "./pages/partnerships/BookstorePartnership";
import LTCPartnership from "./pages/partnerships/LTCPartnership";
import SeniorLivingPartnership from './pages/partnerships/SeniorLivingPartnership';
import PartnerDashboard from "./pages/PartnerDashboard";
import PartnerLogin from "./pages/PartnerLogin";
import PartnerPortal from "./pages/PartnerPortal";
import InfluencerPartnership from "./pages/partnerships/InfluencerPartnership";
import MindfulnessPartnership from "./pages/partnerships/MindfulnessPartnership";
import NonprofitPartnership from "./pages/partnerships/NonprofitPartnership";
import NonprofitTechPartnership from './pages/partnerships/NonprofitTechPartnership';
import ARCPartnership from "./pages/partnerships/ARCPartnership";

// Milestone Notifications
import { MilestoneProvider } from './components/MilestoneToast';

function App() {
  return (
    <BrowserRouter>
      <PartnerHomeGuard />
      <MilestoneProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PlatformPage />} />
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/investors" element={<InvestorsPage />} />
          <Route path="/vc" element={<VCPage />} />
          <Route path="/hero" element={<HeroPage />} />
          <Route path="/apps" element={<AppsIndex />} />
          <Route path="/apps/:slug" element={<AppLanding />} />
          <Route path="/why" element={<WhyEmit />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/home" element={<Home />} />
          <Route path="/hcsm" element={<HCSMPartnership />} />
          <Route path="/ebs" element={<EBSPartnership />} />
          <Route path="/hrm" element={<HRMPartnership />} />
          <Route path="/peo" element={<PEOPartnership />} />
          <Route path="/insureco" element={<InsureCoPartnership />} />
          <Route path="/rehab" element={<RehabPartnership />} />
          <Route path="/chaplain" element={<ChaplainPartnership />} />
          <Route path="/bookstore-partners" element={<BookstorePartnership />} />
          <Route path="/ltc" element={<Navigate to="/senior-living" replace />} />
          <Route path="/senior-living" element={<SeniorLivingPartnership />} />
          <Route path="/influencer" element={<InfluencerPartnership />} />
          <Route path="/mindfulness" element={<MindfulnessPartnership />} />
          <Route path="/partners/arc" element={<ARCPartnership />} />
          <Route path="/nonprofit" element={<NonprofitTechPartnership />} />
          <Route path="/nonprofit-partners" element={<Navigate to="/nonprofit" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/parent-consent/:token" element={<ParentConsent />} />
          <Route path="/parent-consent/:token/form" element={<ParentConsentForm />} />
          <Route path="/admin/consents" element={<ProtectedRoute><AdminConsents /></ProtectedRoute>} />
          <Route path="/care-desk" element={<ProtectedRoute><CareDesk /></ProtectedRoute>} />

          {/* Home App Selection - shown after registration */}
          <Route
            path="/select-home-app"
            element={
              <ProtectedRoute>
                <HomeAppSelection />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Emit */}
          <Route
            path="/emit-the-bible/setup"
            element={
              <ProtectedRoute>
                <EmitProfileSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emit-the-bible"
            element={
              <ProtectedRoute>
                <MinimalDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/emit-research/setup" element={<ProtectedRoute><EmitResearchOnboarding /></ProtectedRoute>} />
          <Route
            path="/emit-research"
            element={
              <ProtectedRoute>
                <EmitResearchChat />
              </ProtectedRoute>
            }
          />
          <Route path="/mindstreams-studio/setup" element={<ProtectedRoute><ChroniclesStudioOnboarding /></ProtectedRoute>} />
          <Route path="/mindstreams-studio" element={<ProtectedRoute><ChroniclesStudioChat /></ProtectedRoute>} />
          <Route path="/mindstreams-studio/mixer" element={<ProtectedRoute><StudioSoundboard /></ProtectedRoute>} />
          <Route path="/mindstreams-studio/soundboard" element={<Navigate to="/mindstreams-studio/mixer" replace />} />
          <Route
            path="/dashboard"
            element={<Navigate to="/emit-the-bible" replace />}
          />
          <Route
            path="/emit"
            element={
              <ProtectedRoute>
                <EmitDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emit-minimal"
            element={
              <ProtectedRoute>
                <MinimalDashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Family Coach */}
          <Route
            path="/family-coach"
            element={
              <ProtectedRoute>
                <FamilyCoachChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/family-coach/setup"
            element={
              <ProtectedRoute>
                <FamilyOnboarding />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Mental Performance */}
          <Route
            path="/mental-performance"
            element={
              <ProtectedRoute>
                <MentalPerformanceChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mental-performance/setup"
            element={
              <ProtectedRoute>
                <MentalPerformanceOnboarding />
              </ProtectedRoute>
            }
          />

          {/* Journey Dashboard */}
          <Route
            path="/journey"
            element={
              <ProtectedRoute>
                <JourneyDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/journey/rules"
            element={
              <ProtectedRoute>
                <JourneyRulesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/safe"
            element={
              <ProtectedRoute>
                <SAFEChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/safe/setup"
            element={
              <ProtectedRoute>
                <SAFEOnboarding />
              </ProtectedRoute>
            }
          />

          {/* Leads Dashboard - whitelist protected */}
          <Route
            path="/partner-dashboard" element={<PartnerDashboard />} />
          <Route path="/partner-login" element={<PartnerLogin />} />
          <Route path="/partner-portal" element={<PartnerPortal />} />
          <Route path="/leads"
            element={
              <ProtectedRoute>
                <LeadsDashboard />
              </ProtectedRoute>
            }
          />

          {/* Testing & Debug Routes - Protected */}
          <Route
            path="/transform-test"
            element={
              <ProtectedRoute>
                <TransformTest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/debug-transform"
            element={
              <ProtectedRoute>
                <DebugTransform />
              </ProtectedRoute>
            }
          />
        </Routes>
      </MilestoneProvider>
    </BrowserRouter>
  );
}

export default App;
